import * as React from 'react'

import { Plus } from 'lucide-react'
import { inject, observer } from 'mobx-react'

import { TableFilters } from '~/client/src/desktop/components/TableFilters'
import DesktopInitialState from '~/client/src/desktop/stores/DesktopInitialState'
import { useTableList } from '~/client/src/desktop/tables/useTableList'
import { CREATE_FORM_RECORD_ID } from '~/client/src/desktop/views/ProjectSetUp/components/ProjectSetupResourceConfig/constants'
import FormPropertiesSidebar from '~/client/src/desktop/views/ProjectSetUp/components/ProjectSetupResourceConfig/FormPropertiesSidebar/FormPropertiesSidebar'
import { Button } from '~/client/src/shared/components/Button/Button'
import SearchInput from '~/client/src/shared/components/Filters/SearchInput/SearchInput'
import * as Layout from '~/client/src/shared/components/Layout'
import { Loader } from '~/client/src/shared/components/Loader'
import { ViewOptionsPopover } from '~/client/src/shared/components/ViewOptions'
import { ResourceTypeId } from '~/client/src/shared/constants/resourceTypes'
import { LocalStorageKey } from '~/client/src/shared/enums/LocalStorageKey'
import SortOrder from '~/client/src/shared/enums/SortOrder'
import Localization from '~/client/src/shared/localization/LocalizationManager'
import type { ResourceSchema } from '~/client/src/shared/resources/domain/resourceSchema'
import ProjectDateStore from '~/client/src/shared/stores/ui/ProjectDate.store'

import { buildResourceFormFields } from './resourceFormFields'
import ResourceFormsActionBar from './ResourceFormsActionBar'
import ResourceFormsTable from './ResourceFormsTable'
import { ResourceFormsColumnId } from './types'
import { useResourceFormRows } from './useResourceFormRows'

interface IProps {
  resourceTypeId: ResourceTypeId
  columnConfigKey: LocalStorageKey
  projectDateStore?: ProjectDateStore
  state?: DesktopInitialState
  resourceSchema: ResourceSchema
}

function ProjectSetupResourceForms({
  resourceTypeId,
  columnConfigKey,
  projectDateStore,
  state,
  resourceSchema,
}: IProps): React.ReactElement | null {
  const projectId = state?.activeProject?.id
  const userId = state?.user?.id
  const [selectedRecordId, setSelectedRecordId] = React.useState<string | null>(
    null,
  )

  const rows = useResourceFormRows(resourceSchema.templates, projectDateStore)

  const rowActionHandlers = React.useMemo(
    () => ({ onEdit: setSelectedRecordId }),
    [],
  )
  const tableFields = React.useMemo(
    () => buildResourceFormFields({ rowActionHandlers }),
    [rowActionHandlers],
  )
  const tableList = useTableList({
    records: rows,
    schema: tableFields.schema,
    displayColumns: tableFields.columns,
    columnConfig: {
      options: tableFields.columnConfig,
      storageKey: columnConfigKey,
      projectId,
      userId,
    },
    selectable: true,
    selectionColumnId: ResourceFormsColumnId.Checkbox,
    initialQuery: {
      groupBy: [],
      sortKey: ResourceFormsColumnId.Name,
      sortOrder: SortOrder.ASC,
    },
    groupingAllowNone: true,
  })

  if (!projectId) return null

  if (resourceSchema.loading && rows.length === 0) {
    return (
      <section className="tw:flex tw:h-full tw:w-full tw:flex-col tw:items-center tw:justify-center">
        <Loader />
      </section>
    )
  }

  const closeSidebar = () => setSelectedRecordId(null)

  return (
    <>
      <Layout.View className="tw:relative tw:overflow-hidden">
        <Layout.Header>
          <div className="tw:flex tw:items-center tw:gap-2 tw:px-4 tw:py-3">
            <SearchInput {...tableList.searchProps} />
            <TableFilters {...tableList.filterProps} />
            <div className="tw:flex-1" />
            <ViewOptionsPopover {...tableList.viewOptionsProps} />
            <Button
              variant="default"
              onClick={() => setSelectedRecordId(CREATE_FORM_RECORD_ID)}
            >
              <Plus data-icon="inline-start" />
              {Localization.translator.newFormButton}
            </Button>
          </div>
        </Layout.Header>
        <Layout.Content>
          <div className="tw:flex tw:h-full tw:min-h-0 tw:flex-col">
            <ResourceFormsActionBar
              selectedRecordId={selectedRecordId}
              selectedRows={tableList.selection.selectedRecords}
              filteredCount={tableList.model.filteredCount}
              clearSelection={tableList.selection.clear}
              selectRecord={setSelectedRecordId}
              resourceSchema={resourceSchema}
            />
            <ResourceFormsTable list={tableList} onEdit={setSelectedRecordId} />
          </div>
        </Layout.Content>
      </Layout.View>

      {selectedRecordId && (
        <FormPropertiesSidebar
          resourceTypeId={resourceTypeId}
          selectedRecordId={selectedRecordId}
          onClose={closeSidebar}
          resourceSchema={resourceSchema}
        />
      )}
    </>
  )
}

export default inject(
  'projectDateStore',
  'state',
)(observer(ProjectSetupResourceForms))
